import { useState } from "react";
import { Users, Loader2, CheckCircle2, Phone } from "lucide-react";
import { toast } from "sonner";
import { Header, Footer } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { http, errMsg, ymd, CABINS } from "../lib/api";

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#EC5B24] transition-colors bg-white";
const FIELDS = [
  ["name", "Your name *", "text"], ["phone", "Mobile number *", "tel"], ["email", "Email *", "email"], ["organisation", "Company / school / group name", "text"],
  ["origin", "From city *", "text"], ["destination", "To city *", "text"], ["depart_date", "Departure date *", "date"], ["return_date", "Return date (optional)", "date"],
];

export default function GroupBooking() {
  const { user, config } = useAuth();
  const [f, setF] = useState({ name: user?.name || "", phone: "", email: user?.email || "", organisation: "", origin: "", destination: "", depart_date: "", return_date: "", travellers: 10, cabin: "economy", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const today = ymd(new Date());

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(f.phone)) return toast.error("Enter a 10-digit mobile number");
    if (+f.travellers < 10) return toast.error("Group bookings start at 10 travellers");
    setBusy(true);
    try {
      const { data } = await http.post("/group-enquiries", { ...f, travellers: +f.travellers, return_date: f.return_date || null, organisation: f.organisation || null, notes: f.notes || null });
      setDone(data.id);
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#F4F6F9]">
      <Header />
      <section className="bg-[#1C1A4A] text-white">
        <div className="mx-auto max-w-5xl px-4 py-12">
          <Users className="h-10 w-10 text-[#EC5B24]" />
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold mt-4 tracking-tight">Group bookings</h1>
          <p className="text-slate-300 mt-3 text-base md:text-lg max-w-2xl">Weddings, school trips, corporate travel or pilgrimages — 10 or more travellers get special group fares, one point of contact and flexible name changes.</p>
        </div>
      </section>
      <div className="mx-auto max-w-5xl px-4 -mt-6 pb-10">
        {done ? (
          <div className="bg-white rounded-2xl p-10 text-center border" data-testid="group-success">
            <CheckCircle2 className="h-12 w-12 mx-auto text-[#0F9D58]" />
            <h2 className="font-heading text-2xl font-bold mt-3">Enquiry received</h2>
            <p className="text-slate-500 mt-2">Reference <b className="font-mono" data-testid="group-ref">{done}</b>. Our team will call you with the best group fare, usually within a few hours.</p>
            {config.support_phone && <a href={`tel:${config.support_phone}`} className="inline-flex items-center gap-2 mt-5 rounded-full bg-[#EC5B24] text-white font-semibold px-5 py-2.5"><Phone className="h-4 w-4" /> Call {config.support_phone}</a>}
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-2xl border p-6 sm:p-8 grid gap-4 sm:grid-cols-2" data-testid="group-form">
            {FIELDS.map(([k, l, type]) => (
              <label key={k} className="text-xs font-semibold text-slate-500">{l}
                <input data-testid={`group-${k}`} type={type} required={l.includes("*")} value={f[k]} min={type === "date" ? (k === "return_date" ? f.depart_date || today : today) : undefined}
                  maxLength={type === "tel" ? 10 : 100} onChange={(e) => setF({ ...f, [k]: type === "tel" ? e.target.value.replace(/\D/g, "") : e.target.value })} className={`${inputCls} mt-1 text-slate-900 font-normal`} />
              </label>
            ))}
            <label className="text-xs font-semibold text-slate-500">Number of travellers (10+) *
              <input data-testid="group-travellers" type="number" min={10} max={500} required value={f.travellers} onChange={(e) => setF({ ...f, travellers: e.target.value })} className={`${inputCls} mt-1 text-slate-900 font-normal`} />
            </label>
            <label className="text-xs font-semibold text-slate-500">Class
              <select data-testid="group-cabin" value={f.cabin} onChange={(e) => setF({ ...f, cabin: e.target.value })} className={`${inputCls} mt-1 text-slate-900 font-normal`}>
                {CABINS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold text-slate-500 sm:col-span-2">Anything else? (preferred airline, timings, budget, meals)
              <textarea data-testid="group-notes" rows={3} maxLength={1000} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={`${inputCls} mt-1 text-slate-900 font-normal`} />
            </label>
            <button data-testid="group-submit-btn" disabled={busy} className="sm:col-span-2 rounded-xl bg-[#EC5B24] hover:bg-[#D84A15] text-white font-heading font-bold text-lg py-3 flex items-center justify-center gap-2 transition-colors">
              {busy && <Loader2 className="h-5 w-5 animate-spin" />} Get group fare quote
            </button>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}
